import { API } from 'src/services'
import { hasPermission } from 'src/utils'
import { Notification } from 'src/common/uiv/notification'
import router from 'src/router'
import Cookies from 'js-cookie'
import { refreshToken } from 'src/components/Cognito'
import { UsuarioIdentificador } from 'src/typescript/utils.ts'
import { formbricksGlobal, ServicoFlagSmithGlobal } from 'src/main'
import { FiltradorDeErros } from 'src/erros'
import servicoLocalStorage from 'src/typescript/servicos/localStorage/localStorage.servico'
import { carregarSecrets } from 'src/typescript/servicos/bifrost/bifrost.servico'
import { inicializarCrisp, atualizarUsuarioCrisp, deveInicializarCrisp } from 'src/typescript/servicos/crisp/crisp.servico'
import { identificarUsuarioPosthog, inicializarPosthog, verificarSeUsuarioPermitido, verificarPosthogEstaAtivo } from 'src/typescript/servicos/posthog/posthog.servico'
import { inicializarUserFlow, identificarUsuarioUserFlow, validarUsuarioUserFlow} from 'src/typescript/servicos/userflow/userflow.servico'
import { inicializadorFirebase } from 'src/typescript/servicos/firebase/firebase.servico'
import i18n from 'src/typescript/servicos/i18n/i18n.servico'
import { obterRotaDeLoginComRedirect } from 'src/router/redirect-login'
import { adicionarErroDePagamentoExpiradoNaSessao, ERRO_PAGAMENTO_EXPIRADO } from 'src/typescript/servicos/http/utils'
import { pinia } from 'src/pinia'
import { useAcessoStore } from '@next/compartilhados/acesso'
import { deveExibirTermosDeUso } from 'src/components/TermosDeUso/termosDeUso'
import { confirmarEmailPendente } from 'src/components/Cognito/contasSalvas'

function normalizarTermosDeTravamento (termos) {
  if (!termos || typeof termos !== 'object') return termos

  // Ações, estados e cabeçalho do R37 são blocos aninhados; todos precisam do mesmo formato.
  // Reconstruir os objetos preserva o payload recebido e mantém os textos sem alterações.
  return Object.fromEntries(Object.entries(termos).map(([chave, valor]) => [
    chave.replace(/_[a-z]/g, trecho => trecho[1].toUpperCase()),
    normalizarTermosDeTravamento(valor)
  ]))
}

function normalizarFrontOptionsDaEmpresa (frontOptions) {
  // Preservar a ausência de customização mantém os textos padrão dos componentes.
  if (!frontOptions?.nomeacaoModuloDeTravamento) return frontOptions

  // A sessão pode trazer os termos em snake_case, enquanto compman-detail usa camelCase.
  // O escopo é apenas a nomenclatura: as demais frontOptions possuem contratos próprios.
  return {
    ...frontOptions,
    nomeacaoModuloDeTravamento: normalizarTermosDeTravamento(frontOptions.nomeacaoModuloDeTravamento)
  }
}

export default {
  mutations: {
    userInfo (index, payload) {
      const roles = []

      for (const role of payload.roles) {
        const module = role[0].split('_')[0]
        const action = role[0].split('_')[1]
        const filter = role[0].split('_')[2]

        roles.push({
          name: role[0],
          module,
          action,
          filter
        })
      }
      // Login e recarga também precisam normalizar os termos, além do fluxo de salvar a customização.
      index.userInfo = {
        ...payload,
        roles,
        compMan: {
          ...payload.compMan,
          frontOptions: normalizarFrontOptionsDaEmpresa(payload.compMan.frontOptions)
        }
      }
    },
    atualizarFrontOptionsDaEmpresa (state, frontOptions) {
      // As duas entradas da sessão entregam camelCase aos consumidores da folha.
      state.userInfo.compMan.frontOptions = normalizarFrontOptionsDaEmpresa(frontOptions)
    },
    integracoesSankhyaInfo (index, payload) {
      index.integracoesSankhyaInfo = { ...payload }
    },
    removeUserInfo (state) {
      state.userInfo = null
      state.usuarioUserFlowPendente = null
    },
    backNotification (state, payload) {
      state.backNotification = payload
    },
    atribuirPossuiTour (state, payload) {
      state.possuiTour = payload
    },
    atribuirUsuarioUserFlowPendente (state, payload) {
      state.usuarioUserFlowPendente = payload
    },
    atribuirAceiteTermosDeUso (state) {
      state.userInfo.compMan.acceptedTerms = true
    },
    atribuirPerfilDeAcesso(state, payload) {
      state.perfilDeAcessoSelecionadoParaMultiPerfis = payload
    },
    atribuirPerfilDeAcessoSelecionadoParaMultiPerfis(state, payload) {
      state.perfilDeAcessoSelecionadoParaMultiPerfis = payload
    }
  },
  actions: {
    validarPermissaoUserFlow ({ state, commit }) {
      const ehGestor = state.userInfo.roleGroup.kind === 'group'
      const ehRH = state.userInfo.roleGroup.kind === 'all'
      const possuiPermissaoEmpregado = hasPermission({module: 'compemployee', action: 'delete'})
      const possuiPermissaoCamposCustomizaveisDaFolha = hasPermission({module: 'camposcustomizaveisdafolha', action: 'delete'})
      const possuiPermissaoCamposCustomizaveis = hasPermission({module: 'camposcustomizaveisdoempregado', action: 'delete'})
      const possuiPermissaoCamposOpcionais = hasPermission({module: 'camposopcionaisdoempregado', action: 'delete'})
      const possuiPermissaoConfiguracoesDePontoEBeneficios = hasPermission({module: 'configuracoesdepontoebeneficiosdoempregado', action: 'delete'})
      const possuiPermissaoDadosCadastrais = hasPermission({module: 'dadoscadastraisdoempregado', action: 'delete'})
      const possuiPermissaoDadosDeTrabalho = hasPermission({module: 'dadosdetrabalhodoempregado', action: 'delete'})

      const ehGestorOuRH = ehGestor || ehRH
      const possuiPermissoes = [
        possuiPermissaoEmpregado,
        possuiPermissaoCamposCustomizaveisDaFolha,
        possuiPermissaoCamposCustomizaveis,
        possuiPermissaoCamposOpcionais,
        possuiPermissaoConfiguracoesDePontoEBeneficios,
        possuiPermissaoDadosCadastrais,
        possuiPermissaoDadosDeTrabalho
      ]
      const resposta = ehGestorOuRH && possuiPermissoes.every(Boolean)
      commit('atribuirPossuiTour', resposta)
      return resposta
    },
    getUserInfo: async ({ commit, state, dispatch, getters }, force = false) => {
      const rotaAtual = router.currentRoute?.value || router.currentRoute

      if (rotaAtual?.meta?.dontNeedAuth && !force) {
        return
      } else if (!state.auth.accessToken && !state.auth.authorization) {
        const authorization = Cookies.get('Authorization')
        const accessToken = Cookies.get('accessToken')
        const userPoolId = Cookies.get('userPoolId')
        const userPoolWebClientId = Cookies.get('userPoolWebClientId')

        if (!authorization && !accessToken) {
          router.push(obterRotaDeLoginComRedirect(router.currentRoute))
          return false
        } else {
          commit('cognitoTokens', {
            authorization,
            accessToken,
            userPoolId,
            userPoolWebClientId
          })
        }
      } else if (state.auth.accessToken) {
        await refreshToken()
      }

      async function obterDadosDoUsuarioLogado () {
        let respostaDaAPI
        try {
          if (!getters.usuarioEstaAutenticado) {
            throw new Error(i18n.t('store.basestates.userInfo.usuarioNaoEncontrado'))
          }

          respostaDaAPI = await API.info.get()
        } catch (erro) {
          respostaDaAPI = { error: erro }
        }
        return respostaDaAPI
      }

      async function obterInformacoesDeIntegracoesSankhya () {
        try {
          // const resultadoApi = await API.novasIntegracoesSankhyaAtivas.get()
          // const integracoesSankhya = resultadoApi.data
          // Estamos mockando a resposta deste endpoint por enquanto, pois estamos tendo um problema com o token de autenticação ao tentar acessar o endpoint.
          const integracoesSankhya = {
            "id": "c350c379-c7ce-4806-bfd3-274c83d47095",
            "bloquear_campos_empregados_e_locais": false,
            "bloquear_campos_empregadores": false,
            "existe_integracao_de_empregados_ativa": false,
            "existe_integracao_de_empregadores_ativa": false,
            "existe_integracao_de_locais_ativa": false
          }
          commit('integracoesSankhyaInfo', integracoesSankhya)
        } catch (erro) {
          FiltradorDeErros.capturarErro(erro)
        }
      }

      let respostaDaAPI = await obterDadosDoUsuarioLogado()

      if (respostaDaAPI.error) {
        if (respostaDaAPI.error.body === ERRO_PAGAMENTO_EXPIRADO) {
            adicionarErroDePagamentoExpiradoNaSessao()
            // O logout captura a URL do Keycloak antes de limpar os dados locais.
            await dispatch('logout', false)
            return false
          }

        respostaDaAPI = await obterDadosDoUsuarioLogado()

        if (respostaDaAPI.error) {
          if (respostaDaAPI.error.body === ERRO_PAGAMENTO_EXPIRADO) {
            adicionarErroDePagamentoExpiradoNaSessao()
            // O logout captura a URL do Keycloak antes de limpar os dados locais.
            await dispatch('logout', false)
            return false
          }

          const respostaEmString = respostaDaAPI.error.data ? JSON.stringify(respostaDaAPI.error.data) : ''
          if (
            respostaDaAPI.error.status === 403 &&
            respostaEmString.includes('nginx') &&
            respostaEmString.includes('403 Forbidden')
          ) {
            await dispatch('logout', false)
            return false
          }
          await dispatch('logout')
          return false
        }
      }


      const idEmpresaUsuario = respostaDaAPI.data.success['0'].compMan.id
      const nomeEmpresaUsuario = respostaDaAPI.data.success['0'].compMan.name
      const idUsuario = respostaDaAPI.data.success['0'].id
      const lastLoginAt = respostaDaAPI.data.success['0'].lastLoginAt
      const currentLoginAt = respostaDaAPI.data.success['0'].currentLoginAt
      const nomeUsuario = respostaDaAPI.data.success['0'].name
      const emailUsuario = respostaDaAPI.data.success['0'].email
      const papelUsuario = respostaDaAPI.data.success['0'].roleGroup.kind
      const possuiIntegracaoAutomaticaCadastrosSankhya = respostaDaAPI.data.success['0'].compMan.possuiIntegracaoAutomaticaCadastrosSankhya
      const possuiModuloSolicitacoes = respostaDaAPI.data.success['0'].compMan.hasApprovals
      const codigoDeAtivacao = respostaDaAPI.data.success['0'].compMan.appKey
      Cookies.set('CompMan', idEmpresaUsuario)
      Cookies.set('UserId', idUsuario)
      servicoLocalStorage.definirIdentificador(idEmpresaUsuario, idUsuario)
      useAcessoStore(pinia).mapearContextoUsuario(respostaDaAPI.data.success['0'])
      commit('userInfo', respostaDaAPI.data.success['0'])
      confirmarEmailPendente(emailUsuario)
      dispatch('updateLegacyCookies')
      dispatch('getBackNotifications')

      localStorage.setItem('angular_session_roles', JSON.stringify(respostaDaAPI.data.success[0].roles.map(i => i[0])))

      const possuiAcompanhamento = state.userInfo?.compMan?.hasAcompanhamento === true
      setTimeout(() => {
        dispatch('gerenciarInicioUsuarioComDadosCompletosEmServicos', { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, currentLoginAt, lastLoginAt, codigoDeAtivacao, possuiModuloSolicitacoes, possuiAcompanhamento })
      }, 100)

      setTimeout(() => dispatch('tioSamNotificacaoSobreVencimento', {}), 6000)

      if (possuiIntegracaoAutomaticaCadastrosSankhya) {
        obterInformacoesDeIntegracoesSankhya()
      }

      return respostaDaAPI.data.success['0']
    },
    async gerenciarInicioUsuarioComDadosCompletosEmServicos ({ dispatch }, { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, lastLoginAt, currentLoginAt, codigoDeAtivacao, possuiModuloSolicitacoes, possuiAcompanhamento }) {
      try {
        await carregarSecrets()

        ServicoFlagSmithGlobal.inicializar(idEmpresaUsuario, nomeEmpresaUsuario)
        inicializadorFirebase({
          possuiAcompanhamento,
          ehLar21: emailUsuario.includes('lar21@'),
          idUsuarioLogado: idUsuario
        })

        const { tamanhoEmpresaUsuario, canal, modulosDisponiveis, servicosDisponiveis, infosImplantacao } = await dispatch('buscarDadosTamanhoCliente', { idUsuario, idEmpresaUsuario, nomeEmpresaUsuario })

        dispatch('enviarInformacoesUsuarioCrisp', { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal, modulosDisponiveis, servicosDisponiveis, codigoDeAtivacao })
        dispatch('enviarInformacoesUsuarioFormbricks', { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal, infosImplantacao })
        const mostrarUserFlow = await dispatch('validarPermissaoUserFlow')
        dispatch('enviarInformacoesUsuarioUserFlow', { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal, lastLoginAt, currentLoginAt, modulosDisponiveis, servicosDisponiveis, mostrarUserFlow })
        if (papelUsuario === 'all' || (papelUsuario === 'mine' && possuiModuloSolicitacoes)) {
          dispatch('enviarInformacoesUsuarioPosthog', { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal })
        }
      } catch (erro) {
        FiltradorDeErros.capturarErro(erro)
      }
    },
    async buscarDadosTamanhoCliente (_, { idEmpresaUsuario }) {
      try {
        const resultadoApi = await API.consultarTamanhoCLiente.save({ idCliente: idEmpresaUsuario })
        return {
          tamanhoEmpresaUsuario: resultadoApi.body.tamanhoCliente || '',
          canal: resultadoApi.body.canal || '',
          modulosDisponiveis: resultadoApi.body.modulosDisponiveis || {},
          servicosDisponiveis: resultadoApi.body.servicosDisponiveis || {},
          infosImplantacao: resultadoApi.body.infosImplantacao || {}
        }
      } catch (erro) {
        FiltradorDeErros.capturarErro(erro, {
          operacao: 'consultar_tamanho_cliente'
        })
        return { tamanhoEmpresaUsuario: '', canal: '', modulosDisponiveis: {}, servicosDisponiveis: {}, infosImplantacao: {}}
      }
    },
    async enviarInformacoesUsuarioCrisp ({state}, { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal, modulosDisponiveis, servicosDisponiveis, codigoDeAtivacao }) {
      const habilitadoNoFlagsmith = state.modulosEFuncionalidades.funcionalidades.acesso_ferramenta_crisp?.enabled === true
      if (deveInicializarCrisp(habilitadoNoFlagsmith)) {
        await inicializarCrisp()
        await atualizarUsuarioCrisp(idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal, modulosDisponiveis, servicosDisponiveis, codigoDeAtivacao)
      }
    },
    async enviarInformacoesUsuarioFormbricks (_, { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal, infosImplantacao }) {
      if (formbricksGlobal.verificarFormbricksEstaAtivo() && tamanhoEmpresaUsuario) {
        if (formbricksGlobal.verificarSeUsuarioPermitido(emailUsuario)) {
          const usuarioParaFormbricks = new UsuarioIdentificador(
            {
              idUsuario,
              nomeUsuario,
              emailUsuario,
              papelUsuario,
              idEmpresaUsuario,
              nomeEmpresaUsuario,
              tamanhoEmpresaUsuario,
              canal,
              infosImplantacao
            }).obterDadosDoUsuario()
          await formbricksGlobal.iniciarFormbricks(usuarioParaFormbricks)
          formbricksGlobal.registrarGatilho('Obteve Dados do Usuário')
        }
      }
    },
    async enviarInformacoesUsuarioPosthog (_, { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal }) {
      if (!tamanhoEmpresaUsuario) return
      if (!verificarSeUsuarioPermitido(emailUsuario)) return

      await inicializarPosthog()
      if (!verificarPosthogEstaAtivo()) return

      const usuarioParaPosthog = new UsuarioIdentificador({
        idUsuario, nomeUsuario, emailUsuario, papelUsuario,
        idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal,
      }).obterDadosDoUsuario()
      await identificarUsuarioPosthog(usuarioParaPosthog)
    },
    async enviarInformacoesUsuarioUserFlow ({ state, commit }, { idUsuario, nomeUsuario, emailUsuario, papelUsuario, idEmpresaUsuario, nomeEmpresaUsuario, tamanhoEmpresaUsuario, canal, lastLoginAt, currentLoginAt, modulosDisponiveis, servicosDisponiveis, mostrarUserFlow, ignorarFiltros}) {
      const usuarioParaUserFlow = new UsuarioIdentificador(
        {
          idUsuario,
          nomeUsuario,
          emailUsuario,
          papelUsuario,
          idEmpresaUsuario,
          nomeEmpresaUsuario,
          tamanhoEmpresaUsuario,
          canal,
          lastLoginAt,
          currentLoginAt,
          modulosDisponiveis,
          servicosDisponiveis
        }).obterDadosDoUsuario()
      await inicializarUserFlow()
      if (validarUsuarioUserFlow(usuarioParaUserFlow, mostrarUserFlow, ignorarFiltros)) {
        // se o usuário não tiver aceitado os termos de uso, aguarda o aceite antes de iniciar o user flow
        if (!ignorarFiltros && deveExibirTermosDeUso(state.userInfo)) {
          commit('atribuirUsuarioUserFlowPendente', usuarioParaUserFlow)
          return
        }

        await identificarUsuarioUserFlow(usuarioParaUserFlow)
      }
    },
    async iniciarUserFlowPendente ({ state, commit }) {
      const usuarioParaUserFlow = state.usuarioUserFlowPendente

      if (!usuarioParaUserFlow) {
        return
      }

      commit('atribuirUsuarioUserFlowPendente', null)
      await identificarUsuarioUserFlow(usuarioParaUserFlow)
    },
    updateLegacyCookies ({ state }) {
      // update cookies that angular use

      const { compMan, name, roles } = state.userInfo

      if (state.userInfo.email.includes('lar21@')) {
        document.cookie = 'isLar21User=' + true
      } else {
        document.cookie = 'isLar21User=' + ''
      }
      window.AngularPermissions = roles
      Cookies.set('login_compman', compMan.id)
      Cookies.set('login_email', name)
      Cookies.set('comp_man_has_tms', compMan.hasArbTms)
      Cookies.set('comp_man_has_whbank', compMan.hasWhbank)
      Cookies.set('comp_man_has_on_call', compMan.hasOnCall)
      Cookies.set('comp_man_has_locker', compMan.canLockFolha)
      Cookies.set('has_recognize_face', compMan.hasRecognizeFace)
      Cookies.set('has_acompanhamento', compMan.hasAcompanhamento)
      Cookies.set('possui_integracao_automatica', compMan.possuiIntegracaoAutomatica)
      Cookies.set('Authorization', state.auth.authorization)
    },
    async getBackNotifications ({ commit }) {
      const result = await API.notification.get()
      if (result.status === 200) {
        Notification.notify({
          type: 'info',
          content: result.data.success
        })
        commit('backNotification', result.data.success)
      }
    },
    async tioSamNotificacaoSobreVencimento ({ state }, { faturas = null }) {
      const compManId = state?.userInfo?.compMan?.id;

      if (!compManId) return null;

      if (
        state.userInfo.email.includes('lar21@') ||
        hasPermission({ module: 'financeiro', action: 'get' })
      ) {
        /** @typedef {import('../../typescript/modulos/tiosam/faturas/dominio/faturas.interface').NotificacaoSobreVencimentoDeFatura} NotificacaoSobreVencimentoDeFatura */

        /** @type {NotificacaoSobreVencimentoDeFatura | null} */
        const notificacao = await API.tioSam.notificacaoSobreVencimento({ compManId, faturas })

        if (notificacao) {
          Notification.notify({
            type: notificacao.tipo,
            content: notificacao.mensagem,
            duration: notificacao.duracao,
          })
        }
      }
    }
  }
}
